import { Component, computed, inject, signal, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TabsModule } from 'primeng/tabs';

import {
  PartnerAppAdminService,
  PartnerAppUser,
  PushRow,
  IssueRow,
  IssueThread,
} from '../../services/partner-app-admin';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';
import { IndDatePipe } from '../../pipes/ind-date';

/** Issue status tag colors: new needs attention, replied is in progress, closed is done. */
const STATUS_SEVERITY: Record<string, 'warn' | 'info' | 'secondary'> = {
  OPEN: 'warn',
  REPLIED: 'info',
  CLOSED: 'secondary',
};

@Component({
  selector: 'app-app-notifications',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TableModule,
    DialogModule,
    SelectModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    TextareaModule,
    TabsModule,
    IndDatePipe,
  ],
  templateUrl: './app-notifications.html',
  styleUrls: ['./app-notifications.css'],
})
export class AppNotificationsPage implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private svc = inject(PartnerAppAdminService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  private route = inject(ActivatedRoute);
  readonly pageTitle = inject(PageTitleService).title;

  // ===================== tabs =====================
  activeTab = signal<'push' | 'issues'>('push');

  setTab(v: string | number | undefined) {
    this.activeTab.set(v === 'issues' ? 'issues' : 'push');
  }

  // ===================== push: compose =====================
  users = signal<PartnerAppUser[]>([]);
  usersLoading = signal(false);
  sending = signal(false);

  /** First option = broadcast (value null), then one entry per partner app user. */
  targetOptions = computed<{ label: string; value: number | null }[]>(() => [
    { label: '📢 All partners (broadcast)', value: null },
    ...this.users().map((u) => ({
      label: `${u.partnerName || u.partnerCode || 'Partner'} — ${u.username}`,
      value: u.userId as number | null,
    })),
  ]);

  form: FormGroup = this.fb.group({
    target: [null as number | null], // null = broadcast
    title: ['', [Validators.required, Validators.maxLength(150)]],
    body: ['', [Validators.required, Validators.maxLength(2000)]],
  });

  // Mirrors of the fields that drive conditional UI (zoneless-safe reactivity).
  fTarget = signal<number | null>(null);
  bodyLen = signal(0);

  // ===================== push: history =====================
  pushes = signal<PushRow[]>([]);
  pushesLoading = signal(false);

  // ===================== issues =====================
  issues = signal<IssueRow[]>([]);
  issuesLoading = signal(false);
  statusFilter = signal<string | null>(null);
  searchText = signal('');
  /** Unread OPEN issues — the Issues tab badge. Only re-synced by UNFILTERED loads. */
  unreadOpen = signal(0);
  private searchTimer: any;
  private threadPollTimer: any;
  private qpSub?: { unsubscribe(): void };
  private lastDeepLinkedIssue = 0;

  readonly statusOptions: { label: string; value: string | null }[] = [
    { label: 'All statuses', value: null },
    { label: 'Open', value: 'OPEN' },
    { label: 'Replied', value: 'REPLIED' },
    { label: 'Closed', value: 'CLOSED' },
  ];

  // ===================== thread dialog =====================
  showThread = signal(false);
  threadLoading = signal(false);
  thread = signal<IssueThread | null>(null);
  replyText = signal('');
  replying = signal(false);
  closing = signal(false);

  constructor() {
    this.form.get('target')!.valueChanges.subscribe((v) => this.fTarget.set(v ?? null));
    this.form.get('body')!.valueChanges.subscribe((v) => this.bodyLen.set((v ?? '').length));
  }

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.loadUsers();
    this.loadPushes();
    this.loadIssues(); // also seeds the unread-OPEN tab badge

    // Deep-link from the admin bell: /app-notifications?tab=issues&issueId=N.
    // Subscribe (not snapshot) — clicking a second bell alert while already on
    // this page must still open the new thread.
    this.qpSub = this.route.queryParamMap.subscribe((qp) => {
      if (qp.get('tab') === 'issues') this.activeTab.set('issues');
      const issueId = Number(qp.get('issueId'));
      if (issueId > 0 && issueId !== this.lastDeepLinkedIssue) {
        this.lastDeepLinkedIssue = issueId;
        this.thread.set(null);
        this.replyText.set('');
        this.showThread.set(true);
        this.loadThread(issueId);
        this.startThreadPolling(issueId);
      }
    });
  }

  ngOnDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.stopThreadPolling();
    this.qpSub?.unsubscribe();
  }

  // ===================== push: compose =====================
  private loadUsers() {
    this.usersLoading.set(true);
    this.svc.partnerUsers().subscribe({
      next: (res: any) => {
        this.usersLoading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to load partner users');
          return;
        }
        this.users.set(Array.isArray(res?.data) ? res.data : []);
      },
      error: () => this.usersLoading.set(false),
    });
  }

  async send() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
    }
    const v = this.form.value;
    const title = (v.title ?? '').trim();
    const body = (v.body ?? '').trim();
    if (!title || !body) {
      this.toast.warning('Both a title and a message are required.');
      return;
    }
    if (v.target == null) {
      const ok = await this.toast.confirm(
        'This notification will be pushed to EVERY partner app user.',
        'Send to ALL partners?',
        'Send to all',
      );
      if (!ok) return;
    }
    this.sending.set(true);
    this.svc.push({ userId: v.target ?? null, title, body }).subscribe({
      next: (res: any) => {
        this.sending.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to send the notification');
          return;
        }
        this.toast.success(res?.message || 'Notification sent');
        // Keep the target so several messages to the same partner are painless.
        this.form.reset({ target: v.target ?? null, title: '', body: '' });
        this.loadPushes();
      },
      error: () => this.sending.set(false),
    });
  }

  // ===================== push: history =====================
  loadPushes() {
    this.pushesLoading.set(true);
    this.svc.pushes(100).subscribe({
      next: (res: any) => {
        this.pushesLoading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to load sent notifications');
          return;
        }
        this.pushes.set(Array.isArray(res?.data) ? res.data : []);
      },
      error: () => this.pushesLoading.set(false),
    });
  }

  // ===================== issues =====================
  loadIssues() {
    this.issuesLoading.set(true);
    const status = this.statusFilter();
    const search = this.searchText().trim();
    this.svc.issues(status, search || null).subscribe({
      next: (res: any) => {
        this.issuesLoading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to load partner issues');
          return;
        }
        const rows: IssueRow[] = Array.isArray(res?.data) ? res.data : [];
        this.issues.set(rows);
        // An unfiltered load sees every issue — re-sync the tab badge from it.
        if (!status && !search) {
          this.unreadOpen.set(rows.filter((r) => !r.isReadByAdmin && r.status === 'OPEN').length);
        }
      },
      error: () => this.issuesLoading.set(false),
    });
  }

  onStatusChange(v: string | null) {
    this.statusFilter.set(v);
    this.loadIssues();
  }

  /** Debounced server-side search (matches title / partner). */
  onSearch(v: string) {
    this.searchText.set(v);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadIssues(), 350);
  }

  statusSeverity(s?: string | null) {
    return STATUS_SEVERITY[(s || '').toUpperCase()] ?? 'secondary';
  }

  snippet(s?: string | null, max = 90): string {
    const t = (s || '').replace(/\s+/g, ' ').trim();
    return t.length > max ? t.slice(0, max) + '…' : t || '—';
  }

  // ===================== thread dialog =====================
  openThread(row: IssueRow) {
    this.thread.set(null);
    this.replyText.set('');
    this.showThread.set(true);
    this.loadThread(row.id);
    this.startThreadPolling(row.id);
    // The GET marks the issue read for the admin server-side — mirror it locally.
    if (!row.isReadByAdmin) {
      this.issues.set(
        this.issues().map((i) => (i.id === row.id ? { ...i, isReadByAdmin: true } : i)),
      );
      if (row.status === 'OPEN') this.unreadOpen.set(Math.max(0, this.unreadOpen() - 1));
    }
  }

  /** Live-update the open thread so a partner's new reply appears within seconds. */
  private startThreadPolling(issueId: number) {
    this.stopThreadPolling();
    this.threadPollTimer = setInterval(() => {
      if (!this.showThread()) { this.stopThreadPolling(); return; }
      this.loadThread(issueId, true);
    }, 4000);
  }

  private stopThreadPolling() {
    if (this.threadPollTimer) { clearInterval(this.threadPollTimer); this.threadPollTimer = undefined; }
  }

  onThreadVisibleChange(v: boolean) {
    this.showThread.set(v);
    if (!v) this.stopThreadPolling();
  }

  closeThread() {
    this.showThread.set(false);
    this.stopThreadPolling();
  }

  // quiet = a background poll: no spinner, and a transient failure must not close
  // the open dialog or toast (the next tick retries).
  private loadThread(issueId: number, quiet = false) {
    if (!quiet) this.threadLoading.set(true);
    this.svc.issueThread(issueId).subscribe({
      next: (res: any) => {
        if (!quiet) this.threadLoading.set(false);
        if (res?.success === false) {
          if (!quiet) {
            this.toast.error(res?.message || 'Failed to load the issue thread');
            this.showThread.set(false);
            this.stopThreadPolling();
          }
          return;
        }
        this.thread.set(res?.data ?? null);
        // The GET marks the issue read server-side; mirror it locally so a
        // deep-linked open (which bypasses openThread) doesn't leave the row
        // bold and the tab badge inflated.
        const row = this.issues().find((i) => i.id === issueId);
        if (row && !row.isReadByAdmin) {
          this.issues.set(
            this.issues().map((i) => (i.id === issueId ? { ...i, isReadByAdmin: true } : i)),
          );
          if (row.status === 'OPEN') this.unreadOpen.set(Math.max(0, this.unreadOpen() - 1));
        }
      },
      error: () => {
        if (!quiet) {
          this.threadLoading.set(false);
          this.showThread.set(false);
        }
      },
    });
  }

  sendReply() {
    const t = this.thread();
    if (!t) return;
    const body = this.replyText().trim();
    if (!body) {
      this.toast.warning('Type a reply first.');
      return;
    }
    this.replying.set(true);
    this.svc.reply({ issueId: t.issue.id, body }).subscribe({
      next: (res: any) => {
        this.replying.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to send the reply');
          return;
        }
        this.toast.success(res?.message || 'Reply sent — the partner will see it in the app');
        this.replyText.set('');
        this.loadThread(t.issue.id);
        this.loadIssues();
      },
      error: () => this.replying.set(false),
    });
  }

  async closeIssue() {
    const t = this.thread();
    if (!t) return;
    const ok = await this.toast.confirm(
      'The issue will be marked CLOSED and the partner will see that in the app. The thread stays readable.',
      'Close issue?',
      'Close issue',
    );
    if (!ok) return;
    this.closing.set(true);
    this.svc.closeIssue(t.issue.id).subscribe({
      next: (res: any) => {
        this.closing.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to close the issue');
          return;
        }
        this.toast.success(res?.message || 'Issue closed');
        this.loadThread(t.issue.id);
        this.loadIssues();
      },
      error: () => this.closing.set(false),
    });
  }
}
